import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { prefetchRoute } from "@/utils/routePrefetch";
import { useBranding } from "@/hooks/useBranding";
import { 
  House, 
  Package, 
  Buildings, 
  Bed, 
  ChartBar, 
  Gear, 
  List, 
  X,
  MoonStars,
  Sun,
  PlusCircle,
  Users as UsersIcon,
  ClockCounterClockwise,
  SignOut,
  Lightning,
  UserCircle,
  Kanban as KanbanIcon,
  BookmarkSimple,
  Handshake,
  Airplane
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth, ROLE_LABELS, hasRole } from "@/contexts/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";

// Menu items with a canonical `key` used for role-based visibility (see Settings > Menu Access).
// Admin ALWAYS sees everything regardless of the visibility map — this is a safeguard against lockout.
const NAV_ITEMS = [
  { to: "/",            label: "Dashboard",  icon: House,                    key: "dashboard" },
  { to: "/my-referrals",label: "My Dashboard", icon: Handshake,              key: "my_referrals" },
  { to: "/quick-quote", label: "Quick Quote",icon: Lightning,                key: "quick_quote" },
  { to: "/packages",    label: "Packages",   icon: Package,                  key: "packages" },
  { to: "/pipeline",    label: "Pipeline",   icon: KanbanIcon,               key: "pipeline" },
  { to: "/templates",   label: "Templates",  icon: BookmarkSimple,           key: "templates" },
  { to: "/customers",   label: "Customers",  icon: UserCircle,               key: "customers" },
  { to: "/vendors",     label: "Vendors",    icon: Buildings,                key: "vendors" },
  { to: "/hotels",      label: "Hotels",     icon: Bed,                      key: "hotels" },
  { to: "/reports",     label: "Reports",    icon: ChartBar,                 key: "reports" },
  { to: "/referrers",   label: "Referrers",  icon: Handshake,                key: "referrers" },
  { to: "/master-data", label: "Master Data",icon: Airplane,                 key: "master_data" },
  { to: "/users",       label: "Users",      icon: UsersIcon,                key: "users" },
  { to: "/audit-log",   label: "Audit Log",  icon: ClockCounterClockwise,    key: "audit_log" },
  { to: "/settings",    label: "Settings",   icon: Gear,                     key: "settings" },
];

// Default visibility used until /api/settings responds. Kept in sync with backend DEFAULT_MENU_VISIBILITY.
const DEFAULT_MENU_VISIBILITY = {
  dashboard:   ["admin", "manager", "sales_agent", "accounts"],
  quick_quote: ["admin", "manager", "sales_agent"],
  packages:    ["admin", "manager", "sales_agent", "accounts"],
  pipeline:    ["admin", "manager", "sales_agent"],
  templates:   ["admin", "manager", "sales_agent"],
  customers:   ["admin", "manager", "sales_agent"],
  vendors:     ["admin", "manager"],
  hotels:      ["admin", "manager", "sales_agent"],
  reports:     ["admin", "manager", "accounts"],
  referrers:   ["admin", "manager"],
  my_referrals: ["referrer"],
  master_data: ["admin", "manager"],
  users:       ["admin"],
  audit_log:   ["admin", "manager"],
  settings:    ["admin"],
};

const filterNav = (user, menuVisibility) => {
  if (!user) return [];
  // Admin sees everything EXCEPT items exclusively meant for the referrer role.
  if (user.role === "admin") return NAV_ITEMS.filter(i => i.key !== "my_referrals");
  const vis = menuVisibility || DEFAULT_MENU_VISIBILITY;
  const overrides = user.menu_overrides || {};
  return NAV_ITEMS.filter(item => {
    // Per-user override takes precedence
    if (Object.prototype.hasOwnProperty.call(overrides, item.key)) return !!overrides[item.key];
    const roles = vis[item.key] || DEFAULT_MENU_VISIBILITY[item.key] || [];
    return roles.includes(user.role);
  });
};

const Layout = () => {
  const navigate = useNavigate();
  const { user, logout, menuVisibility } = useAuth();
  const branding = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [dark]);

  const nav = filterNav(user, menuVisibility);
  const userInitial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen brand-gradient-soft">
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed left-0 top-0 z-40 h-screen w-64 border-r border-border brand-sidebar transition-transform lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-5">
          <div className="flex items-center gap-2.5" data-testid="sidebar-brand">
            {branding.logo ? (
              <img src={branding.logo} alt="Logo" className="h-10 w-10 rounded-xl object-cover" />
            ) : (
              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl brand-gradient text-white gold-glow">
                <Airplane size={20} weight="fill" className="-rotate-45" />
              </div>
            )}
            <div>
              <div className="font-display font-bold text-[15px] leading-none tracking-tight" data-testid="sidebar-brand-name">
                {branding.companyName?.split(" ").slice(0, 2).join(" ") || "Aynoor Travels"}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-widest">Travel ERP</div>
            </div>
          </div>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-8rem-80px)]">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setSidebarOpen(false)}
              onMouseEnter={() => prefetchRoute(item.key)}
              onFocus={() => prefetchRoute(item.key)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                  isActive
                    ? "nav-active"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:translate-x-0.5"
                )
              }
            >
              <item.icon size={18} weight="regular" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          {user?.role !== "referrer" && (
            <Button
              onClick={() => navigate('/packages/new')}
              data-testid="new-package-sidebar-btn"
              className="w-full btn-gold h-10 font-semibold"
            >
              <PlusCircle size={16} className="mr-2" weight="bold" />
              New Package
            </Button>
          )}
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between brand-header-gradient backdrop-blur-xl px-4 sm:px-6 lg:px-8">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-open"
          >
            <List size={24} />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle"
            >
              {dark ? <Sun size={18} weight="bold" className="text-gold-hi" /> : <MoonStars size={18} weight="bold" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 border-l border-border pl-3 pr-1 py-1 rounded-md hover:bg-accent transition-colors" data-testid="user-menu-trigger">
                  <div className="h-8 w-8 rounded-full brand-gradient flex items-center justify-center text-white font-semibold text-sm gold-glow">
                    {userInitial}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium leading-none">{user?.name || "User"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal mt-0.5">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasRole(user, "admin") && (
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-menu-settings">
                    <Gear size={14} className="mr-2" /> Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="user-menu-logout">
                  <SignOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
